import { makeStyles, Tooltip } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissCircleRegular, MicRegular } from '@fluentui/react-icons';
import { SendHorizonal, Mic } from 'lucide-react';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { IUserPreferences, UserPreferences } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { useFeature, useUserPreferences } from '../../store/hooks';
import {
    EventName_UserInteraction,
    EventProperty_Value_ApplicationType,
    EventProperty_Value_MicrophoneClick,
    UserInteractionEventProperties,
} from '../../store/models/customEvents';
import './SendButton.scss';
import SyButton from '../Common/SyButton';

const useStyles = makeStyles({
    recordingButtons: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export interface SendButtonProps {
    mode: 'ready' | 'typing' | 'listening' | 'processing';
    onClick: () => void;
    onChangeQuery: (query: string) => void;
    onSendQuery: () => void;
    currentQuery?: string;
}

// manages the send button and the voice control
const SendButton: FC<SendButtonProps> = ({ mode, onChangeQuery, onSendQuery, currentQuery = '' }: SendButtonProps) => {
    const classes = useStyles();
    const [token, setToken] = useState('');
    const [tokenExpiresOn, setTokenExpiresOn] = useState<Date | null>(null);
    const [currentMode, setCurrentMode] = useState<'ready' | 'typing' | 'listening' | 'processing'>('ready');
    const [sendMessage, setSendMessage] = useState<boolean>(false);
    const [recognizer, setRecognizer] = useState<sdk.SpeechRecognizer | null>(null);
    const isStoppingManuallyRef = useRef<boolean>(false);
    const { dispatchNotification } = useNotification();
    const chatFeature = useFeature('chat');
    const speechToTextSupportedLanguages = chatFeature.speechToText?.languages;
    const speechToTextRegion = chatFeature.speechToText?.region;
    const isSpeechToTextEnabled = chatFeature.speechToText?.isEnabled;
    const speechResourceId = chatFeature.speechToText?.resourceId;
    const silenceTimeout = chatFeature.speechToText?.silenceTimeout;
    const { userPreferences, dispatch } = useUserPreferences();
    const showLanguageSelectionInPrompt = userPreferences?.showLanguageSelectionInPrompt ?? true;
    // Preserve user's preferred language if set, otherwise default to 'auto' (auto-detect)
    const [selectedLanguage, setSelectedLanguage] = useState<string>(
        userPreferences?.speechToTextLanguage ?? 'auto', //?? (chatFeature && chatFeature.speechToText?.languages?.find((l) => l.locale == navigator.language)?.locale) ?? 'en-US',
    );

    const { userProfile } = useUserContext();
    const [audioInputAvailable, setAudioInputAvailable] = useState<boolean | null>(null);

    // Detect audio input devices and microphone permission
    useEffect(() => {
        let cancelled = false;
        async function checkAudioInput() {
            try {
                // Check for audioinput device
                const devices = await navigator.mediaDevices.enumerateDevices();
                const hasAudioInput = devices.some((device) => device.kind === 'audioinput');
                if (!hasAudioInput) {
                    if (!cancelled) setAudioInputAvailable(false);
                    return;
                }
                // Check for microphone permission if Permissions API is available
                if (navigator.permissions && navigator.permissions.query) {
                    try {
                        const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                        if (!cancelled) {
                            if (status.state === 'denied') setAudioInputAvailable(false);
                            else setAudioInputAvailable(true);
                        }
                    } catch {
                        // If Permissions API fails, fallback to try getUserMedia
                        try {
                            await navigator.mediaDevices.getUserMedia({ audio: true });
                            if (!cancelled) setAudioInputAvailable(true);
                        } catch {
                            if (!cancelled) setAudioInputAvailable(false);
                        }
                    }
                } else {
                    // Fallback: try getUserMedia
                    try {
                        await navigator.mediaDevices.getUserMedia({ audio: true });
                        if (!cancelled) setAudioInputAvailable(true);
                    } catch {
                        if (!cancelled) setAudioInputAvailable(false);
                    }
                }
            } catch {
                if (!cancelled) setAudioInputAvailable(false);
            }
        }
        checkAudioInput();
        return () => {
            cancelled = true;
        };
    }, []);

    // get the speech service token
    const fetchSpeechServiceToken = useCallback(async () => {
        try {
            const content = await performEnterpriseGPTApiCall(async (client, signal) => {
                return await client.getSpeechServiceToken(signal);
            });
            setToken(content!.token!);
            setTokenExpiresOn(new Date(content!.expiresOn!));
            return content!.token;
        } catch (error) {
            appInsights.trackException({ exception: error as Error });
            dispatchNotification('error.api.get.speechServiceToken', 'error');
        }
    }, [dispatchNotification, setTokenExpiresOn]);

    useEffect(() => {
        if (currentMode !== 'listening') {
            setCurrentMode(mode);
        }
    }, [mode, currentMode]);

    useEffect(() => {
        // Recording is completed, send the query
        if (sendMessage) {
            setSendMessage(false);
            onSendQuery();
        }
    }, [sendMessage, onSendQuery]);

    const onUpdateUserPreferencesLanguage = useCallback(
        (locale: string) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    setSelectedLanguage(locale);
                    // Update user preferences with the locale language
                    const updatedUserPreferences: IUserPreferences = {
                        ...userPreferences,
                        speechToTextLanguage: locale,
                    };
                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    dispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (error) {
                    appInsights.trackException({ exception: error as Error });
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                }
            });
        },
        [userPreferences, dispatch, dispatchNotification],
    );

    const refreshTokenIfNeeded = useCallback(async () => {
        const nowUtc = new Date(new Date().toUTCString());
        if (!tokenExpiresOn || nowUtc >= tokenExpiresOn) {
            return await fetchSpeechServiceToken();
        } else return token;
    }, [token, tokenExpiresOn, fetchSpeechServiceToken]);

    const setIsStoppingManually = (value: boolean) => {
        isStoppingManuallyRef.current = value;
    };

    const onStartSpeechToText = useCallback(async () => {
        const currentToken = await refreshTokenIfNeeded();
        const authToken = `aad#${speechResourceId}#${currentToken}`;
        const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(authToken, speechToTextRegion!);
        let recognizerInstance: sdk.SpeechRecognizer;
        const userSilenceTimeout = userPreferences?.speechClickSend
            ? (silenceTimeout?.toString() ?? '3000')
            : ((userPreferences?.speechAutoSendNumSecs ?? 3) * 1000).toString();

        if (selectedLanguage === 'auto') {
            // Use only the auto-detect supported languages for recognition, get the first 4 languages
            const configs = (speechToTextSupportedLanguages?.slice(0, 4) ?? []).map((lang) =>
                sdk.SourceLanguageConfig.fromLanguage(lang.locale ?? 'en-US', lang.customSpeechModelEndpointId ?? undefined),
            );

            const autoDetectLangConfig = sdk.AutoDetectSourceLanguageConfig.fromSourceLanguageConfigs(configs);
            speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, userSilenceTimeout);
            const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
            recognizerInstance = sdk.SpeechRecognizer.FromConfig(speechConfig, autoDetectLangConfig, audioConfig);
        } else {
            // Manual language selection
            speechConfig.speechRecognitionLanguage = selectedLanguage;
            const endpointId = speechToTextSupportedLanguages?.find(
                (l) => l.locale === selectedLanguage,
            )?.customSpeechModelEndpointId;
            if (endpointId) {
                speechConfig.endpointId = endpointId;
            }
            speechConfig.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, userSilenceTimeout);
            const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
            recognizerInstance = new sdk.SpeechRecognizer(speechConfig, audioConfig);
        }

        setRecognizer(recognizerInstance);
        setIsStoppingManually(false);

        /**
         * REMOVE COURANT DYSFLUES (hesitations, rehearsals) of a text
         * @param text Texte to clean
         * @param language Language code to determine which dysfluency words to use
         * @returns Text without dysfluences
         */
        const removeDysfluencies = (text: string, language: string): string => {
            if (!text) return '';

            // Get language code from locale (e.g., 'fr-FR' -> 'fr')
            const langCode = language.split('-')[0].toLowerCase();

            // Dysfluency words that should be removed based on language
            let dysfluencyWords: string[] = [];

            switch (langCode) {
                case 'fr': // French
                    dysfluencyWords = ['euh', 'heu', 'ben', 'bah', 'hmm', 'umm', 'mmm'];
                    break;
                    // case 'de': // German
                    //     dysfluencyWords = ['äh', 'ähm', 'eh', 'ehm'];
                    //     break;
                    // case 'it': // Italian
                    //     dysfluencyWords = ['eh', 'ehm', 'beh', 'boh'];
                    //     break;
                    // case 'es': // Spanish
                    //     dysfluencyWords = ['eh', 'ehm'];
                    //     break;
                    // case 'zh': // Chinese
                    //     dysfluencyWords = ['嗯', '呃'];
                    break;
                case 'en': // English
                default:
                    dysfluencyWords = ['um', 'uh', 'eh', 'hmm', 'umm', 'mmm'];
                    break;
            }

            // List of current dysfluences (can be extended as required)
            const dysfluencies = [
                new RegExp(`\\b(${dysfluencyWords.join('|')})\\b`, 'gi'), // Common dysfluency words
                /(\b\w+\b)(\s+\1\b)+/gi, // Consecutive word repetitions
                /\.\.\./g, // Ellipses
                /\s{2,}/g, // Multiple spaces
            ];

            let cleanedText = text;

            // Removing some dysfluences
            dysfluencies.forEach((pattern) => {
                if (pattern.toString().includes('(\\b\\w+\\b)')) {
                    // Special treatment for repetitions
                    cleanedText = cleanedText.replace(pattern, '$1');
                } else {
                    cleanedText = cleanedText.replace(pattern, ' ');
                }
            });

            // Final cleaning of spaces
            cleanedText = cleanedText.replace(/\s{2,}/g, ' ').trim();

            return cleanedText;
        };

        recognizerInstance.recognizing = (_, event) => {
            const cleanText = removeDysfluencies(event.result.text, selectedLanguage);
            // Always combine with current query from the text zone
            const combinedText = currentQuery ? `${currentQuery} ${cleanText}` : cleanText;
            onChangeQuery(combinedText);
        };

        recognizerInstance.recognized = (_, event) => {
            const cleanText = removeDysfluencies(event.result.text, selectedLanguage);
            const recognizedText = cleanText;
            if (isStoppingManuallyRef.current) {
                setIsStoppingManually(false);
                onChangeQuery('');
            } else if (recognizedText) {
                // Always combine with current query from the text zone
                const combinedText = currentQuery ? `${currentQuery} ${recognizedText}` : recognizedText;
                onChangeQuery(combinedText);

                // Only auto-send if speechClickSend is false (automatic send is enabled)
                if (!userPreferences?.speechClickSend) {
                    setSendMessage(true);
                }
            }
            recognizerInstance.stopContinuousRecognitionAsync(
                () => {
                    setCurrentMode('ready');
                    setRecognizer(null);
                },
                (error) => {
                    appInsights.trackException({ exception: new Error(`Error stopping recognition: ${error}`) });
                    setCurrentMode('ready');
                    setRecognizer(null);
                },
            );
        };
        recognizerInstance.canceled = (_, event) => {
            console.error('SpeechRecognizer canceled:', event);
            if (event.errorDetails) {
                console.error('Error details:', event.errorDetails);
            }
        };

        recognizerInstance.sessionStopped = (_, event) => {
            console.log('SpeechRecognizer session stopped:', event);
        };
        recognizerInstance.startContinuousRecognitionAsync(
            () => {
                setCurrentMode('listening');
            },
            (error) => {
                recognizerInstance.stopContinuousRecognitionAsync();
                appInsights.trackException({ exception: new Error(error) });
                dispatchNotification('error.api.common.speechRecognitionFailed', 'error');
                setCurrentMode('ready');
                setRecognizer(null);
                setIsStoppingManually(false);
            },
        );

        appInsights.trackEvent({
            name: EventName_UserInteraction,
            properties: {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_MicrophoneClick,
            } as UserInteractionEventProperties,
        });
    }, [
        onChangeQuery,
        selectedLanguage,
        speechToTextRegion,
        silenceTimeout,
        refreshTokenIfNeeded,
        dispatchNotification,
        userProfile,
        userPreferences,
        currentQuery,
        speechResourceId,
        speechToTextSupportedLanguages,
    ]);

    const onStopAndCancelSpeechToText = useCallback(() => {
        if (!recognizer) return;
        setIsStoppingManually(true);
        // Reset query when stopping
        onChangeQuery('');
        recognizer.stopContinuousRecognitionAsync(
            () => {
                setCurrentMode('ready');
                setRecognizer(null);
                setIsStoppingManually(false);
            },
            (error) => {
                appInsights.trackException({ exception: new Error(`Error stopping recognition: ${error}`) });
                setCurrentMode('ready');
                setRecognizer(null);
                setIsStoppingManually(false);
            },
        );
    }, [recognizer, onChangeQuery]);

    const onStopToSendOrValidateSpeechToText = useCallback(() => {
        if (!recognizer) return;
        setIsStoppingManually(false);
        // Don't reset accumulated text when validating - keep it for aggregation
        recognizer.stopContinuousRecognitionAsync(
            () => {
                setCurrentMode('ready');
                setRecognizer(null);
                setIsStoppingManually(false);
            },
            (error) => {
                appInsights.trackException({ exception: new Error(`Error stopping recognition for validation: ${error}`) });
                setCurrentMode('ready');
                setRecognizer(null);
                setIsStoppingManually(false);
            },
        );
    }, [recognizer]);

    // Wrapper for onSendQuery
    const handleSendQuery = useCallback(() => {
        onSendQuery();
    }, [onSendQuery]);

    const renderMicrophoneButton = useCallback(() => {
        // Show microphone button only if SpeechToText is enabled and mode is ready or typing
        if (!isSpeechToTextEnabled || currentMode === 'processing') {
            return null;
        }

        if (currentMode === 'listening') {
            return (
                <>
                    <div className={classes.recordingButtons}>
                        <Tooltip content={`Stop recording and cancel the query`} relationship='label'>
                            <SyButton
                                icon={<DismissCircleRegular />}
                                appearance='primary'
                                size='large'
                                onClick={onStopAndCancelSpeechToText}
                            />
                        </Tooltip>
                        <Tooltip content={`Stop recording `} relationship='label'>
                            <SyButton
                                icon={<CheckmarkCircleRegular />}
                                appearance='primary'
                                size='large'
                                onClick={onStopToSendOrValidateSpeechToText}
                                style={{ marginLeft: 5 }}
                            />
                        </Tooltip>
                    </div>
                </>
            );
        }

        // If audio input is not available or permission denied, disable and show tooltip
        if (audioInputAvailable === false) {
            return (
                <Tooltip content={'Unavailable, modify your browser or system audio configurations'} relationship='label'>
                    <SyButton icon={<Mic />} appearance='outline' size='large' disabled />
                </Tooltip>
            );
        }

        // Render the microphone button (single button, no language selection UI)
        return (
            <Tooltip content={'Start talking'} relationship='label'>
                <SyButton
                    data-testid='start-talking'
                    icon={<Mic fontSize={40} className='sy-mic-btn-icon' />}
                    appearance='outline'
                    size='large'
                    iconOnly={true}
                    onClick={onStartSpeechToText}
                    className='sy-mic-btn-border'
                />
            </Tooltip>
        );
    }, [
        currentMode,
        isSpeechToTextEnabled,
        audioInputAvailable,
        onStartSpeechToText,
        onStopAndCancelSpeechToText,
        onStopToSendOrValidateSpeechToText,
        classes.recordingButtons,
        onUpdateUserPreferencesLanguage,
        selectedLanguage,
        showLanguageSelectionInPrompt,
        speechToTextSupportedLanguages,
    ]);

    const renderSendButton = useCallback(() => {
        // Show send button ONLY if there is text AND mode is ready or typing (not processing)
        if (currentMode === 'ready' || currentMode === 'typing') {
            return (
                <Tooltip content={'Send message'} relationship='label'>
                    <SyButton
                        icon={<SendHorizonal className='sy-send-icon' />}
                        appearance='primary'
                        size='large'
                        iconOnly={true}
                        onClick={handleSendQuery}
                        className='sy-send-btn-border'
                    />
                </Tooltip>
            );
        }
        // For all other cases, hide the button
        return null;
    }, [currentMode, handleSendQuery, currentQuery]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0 }}>
            {renderMicrophoneButton()}
            {renderSendButton()}
        </div>
    );
};

export default SendButton;
